import { type AriaRole, type CSSProperties, useId, useMemo } from "react";

/**
 * Shape of one migrated SVG figure. The inner content (generated engraving
 * geometry — meridian polylines, tick rings, dimension text) is kept verbatim
 * as a string and injected via dangerouslySetInnerHTML. The OUTER <svg> is a
 * real JSX element carrying its own attributes, and because React never
 * hydrates dangerouslySetInnerHTML content this is inherently free of
 * hydration mismatches.
 */
export type EngravingData = {
  viewBox?: string;
  role?: AriaRole;
  ariaLabel?: string;
  ariaHidden?: boolean;
  className?: string;
  styleText?: string;
  width?: string;
  height?: string;
  inner: string;
};

/** Parse an inline CSS `style="a:b;c:d"` string into a React style object. */
function parseStyleText(text?: string): CSSProperties | undefined {
  if (!text) return undefined;
  const out: Record<string, string> = {};
  for (const decl of text.split(";")) {
    const idx = decl.indexOf(":");
    if (idx === -1) continue;
    const prop = decl.slice(0, idx).trim();
    const value = decl.slice(idx + 1).trim();
    if (!prop) continue;
    const camel = prop.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
    out[camel] = value;
  }
  return out as CSSProperties;
}

const SVG_ID_ATTRIBUTE = /\bid=(["'])([^"']+)\1/g;
const SVG_URL_REFERENCE = /url\(\s*(["']?)#([^)\s"']+)\1\s*\)/g;
const SVG_HREF_REFERENCE = /\b(href|xlink:href)=(["'])#([^"']+)\2/g;

/**
 * Scope SVG fragment ids to one rendered engraving so repeated concept
 * specimens cannot resolve a marker, gradient or symbol from a sibling SVG.
 */
export function namespaceSvgIds(inner: string, prefix: string): string {
  const ids = new Set<string>();
  for (const match of inner.matchAll(SVG_ID_ATTRIBUTE)) {
    const id = match[2];
    if (id) ids.add(id);
  }
  if (ids.size === 0) return inner;

  const namespaced = (id: string) => `${prefix}-${id}`;
  return inner
    .replace(SVG_ID_ATTRIBUTE, (original, quote: string, id: string) =>
      ids.has(id) ? `id=${quote}${namespaced(id)}${quote}` : original,
    )
    .replace(
      SVG_URL_REFERENCE,
      (original, quote: string, id: string) =>
        ids.has(id) ? `url(${quote}#${namespaced(id)}${quote})` : original,
    )
    .replace(
      SVG_HREF_REFERENCE,
      (original, attribute: string, quote: string, id: string) =>
        ids.has(id)
          ? `${attribute}=${quote}#${namespaced(id)}${quote}`
          : original,
    );
}

/**
 * Render one migrated engraving. `extraClassName` lets a caller add layout
 * classes on top of the figure's own class (e.g. sizing wrappers).
 */
export function Engraving({
  data,
  extraClassName,
  extraStyle,
}: {
  data: EngravingData;
  extraClassName?: string;
  extraStyle?: CSSProperties;
}) {
  const instanceId = useId().replace(/[^a-zA-Z0-9]/g, "");
  const inner = useMemo(
    () => namespaceSvgIds(data.inner, `engraving-${instanceId}`),
    [data.inner, instanceId],
  );
  const className =
    [data.className, extraClassName].filter(Boolean).join(" ") || undefined;
  const style = { ...parseStyleText(data.styleText), ...extraStyle };
  return (
    <svg
      viewBox={data.viewBox}
      role={data.role}
      aria-label={data.ariaLabel}
      aria-hidden={data.ariaHidden}
      width={data.width}
      height={data.height}
      className={className}
      style={Object.keys(style).length ? style : undefined}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Engraving geometry is static, module-owned SVG data.
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
}
